"use client";

import { useState } from "react";
import {
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import {
  DependentPanel,
  IndependentPanel,
  SeveralPanel,
  SinglePanel,
} from "@/components/tools/stats-regression/comparison-panels";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput, parseNumberList } from "@/lib/parse-number";
import { LINE_BREAK } from "@/lib/tool-kit/stats-descriptive/inputs";
import type { Alternative } from "@/lib/tool-kit/stats-regression/correlation-tests";

/** English strings. A translated content file overrides these through `ui`. */
export const CORRELATION_COMPARISON_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to do?",
  modeSingle: "Confidence interval for one r",
  modeIndependent: "Compare two independent correlations",
  modeDependent: "Compare two dependent correlations",
  modeSeveral: "Compare several correlations (pooled r)",
  levelLabel: "Confidence level",
  level90: "90%",
  level95: "95%",
  level99: "99%",
  alternativeLabel: "Alternative hypothesis",
  altTwoSided: "Two-sided (different)",
  altGreater: "One-sided (first greater)",
  altLess: "One-sided (first smaller)",
  tryExample: "Try an example",
  clear: "Clear",
  // Fields
  rLabel: "Sample correlation r",
  nLabel: "Sample size n",
  rho0Label: "Hypothesised ρ (optional)",
  r1Label: "r₁ (first sample)",
  n1Label: "n₁",
  r2Label: "r₂ (second sample)",
  n2Label: "n₂",
  rjkLabel: "r_jk — shared variable j with k",
  rjhLabel: "r_jh — shared variable j with h",
  rkhLabel: "r_kh — between the other two variables",
  nSameLabel: "n (the same people for all three)",
  severalLabel: "One study per line: r, n",
  severalPlaceholder: "0.30, 50\n0.45, 80\n0.52, 65",
  severalCount: "one: study read|other: studies read",
  skipped:
    "one: , {count} line skipped (needs r and n)|other: , {count} lines skipped (need r and n)",
  dependentHint:
    "Use this when both correlations share one variable and come from the same participants — for example how well a test predicts a grade versus how well a second test does.",
  independentHint:
    "Use this when the two correlations come from different groups of participants.",
  // Messages
  enterPrompt:
    "Enter valid values: each r must lie strictly between −1 and 1 and each n must be greater than 3.",
  impossibleMatrix:
    "These three correlations cannot all occur together: they do not form a valid correlation matrix (its determinant is not positive). Check r_kh.",
  results: "Results",
  // Single
  singleCi: "{level} confidence interval for ρ",
  singleFisherZ: "Fisher z",
  singleSe: "Standard error of z",
  singleDf: "Degrees of freedom (n − 2)",
  singleTestZ: "z against ρ₀",
  singleTestP: "p-value",
  singleSummary:
    "With r = {r} from {n} cases, the plausible values of the population correlation run from {low} to {high} at {level} confidence.",
  singleTestSummary: "Against ρ₀ = {rho0}: z = {z}, p {p}.",
  singleApa: "r({df}) = {r}, {level} CI [{low}, {high}]",
  singleWorking:
    "z = atanh(r) = {z}; SE = 1 ÷ √(n − 3) = 1 ÷ √{m} = {se}; interval = tanh(z ± {crit} × SE) = {low} to {high}",
  singleTestWorking:
    "z = (atanh(r) − atanh(ρ₀)) ÷ SE = ({z} − {z0}) ÷ {se} = {stat}",
  // Independent
  indZ: "z",
  indP: "p-value",
  indDiff: "Difference r₁ − r₂",
  indDiffCi: "{level} CI for the difference",
  indCi1: "{level} CI for ρ₁",
  indCi2: "{level} CI for ρ₂",
  indSummaryDifferent:
    "The two correlations differ significantly at the 5% level.",
  indSummarySame:
    "The difference is not statistically significant at the 5% level: the data are compatible with one common correlation.",
  indApa: "r₁ = {r1} (n = {n1}), r₂ = {r2} (n = {n2}), z = {z}, p {p}",
  indWorking:
    "z = (atanh r₁ − atanh r₂) ÷ √(1 ÷ (n₁ − 3) + 1 ÷ (n₂ − 3)) = ({z1} − {z2}) ÷ √(1 ÷ {m1} + 1 ÷ {m2}) = ({z1} − {z2}) ÷ {se} = {z}",
  indZouNote:
    "The interval for the difference is Zou's (2007) method, which combines the two Fisher intervals; it does not assume the difference is normal.",
  // Dependent
  depSteiger: "Steiger's Z",
  depSteigerP: "p (Steiger)",
  depWilliams: "Williams's t",
  depWilliamsP: "p (Williams)",
  depHotelling: "Hotelling's t",
  depHotellingP: "p (Hotelling)",
  depDiff: "Difference r_jk − r_jh",
  depDf: "Degrees of freedom (t tests)",
  depSummary:
    "Steiger's Z = {z}, p {p}. Williams's t and Hotelling's t, the classic tests, give p {pw} and p {ph}.",
  depSummaryDifferent:
    "The two correlations with the shared variable differ significantly at the 5% level.",
  depSummarySame:
    "The difference is not statistically significant at the 5% level.",
  depApa:
    "Steiger’s Z = {z}, p {p} (r_jk = {rjk}, r_jh = {rjh}, r_kh = {rkh}, n = {n})",
  depWorking:
    "With r̄ = {mean}: ψ = r_kh(1 − 2r̄²) − ½r̄²(1 − 2r̄² − r_kh²) = {psi}, covariance term c = ψ ÷ (1 − r̄²)² = {c}",
  depWorking2:
    "Z = (atanh r_jk − atanh r_jh) × √(n − 3) ÷ √(2 − 2c) = ({z1} − {z2}) × √{m} ÷ √{denominator} = {z}",
  depWilliamsNote:
    "Williams's t is the recommended t test for dependent correlations; Hotelling's is shown for completeness and runs slightly liberal in small samples.",
  // Several
  sevQ: "Q (heterogeneity)",
  sevDf: "Degrees of freedom",
  sevP: "p-value",
  sevI2: "I² (inconsistency)",
  sevPooled: "Pooled r",
  sevPooledCi: "{level} CI for pooled r",
  sevTable: "Studies and weights",
  colStudy: "Study",
  colR: "r",
  colN: "n",
  colZ: "Fisher z",
  colWeight: "Weight",
  studyLabel: "Study {number}",
  sevSummaryDifferent:
    "The correlations differ more than sampling error alone would explain (p < .05), so a single pooled r hides real variation.",
  sevSummarySame:
    "The correlations are consistent with one common value: the differences are within what sampling error produces.",
  sevApa:
    "Q({df}) = {q}, p {p}, I² = {i2}; pooled r = {pooled}, {level} CI [{low}, {high}]",
  sevWorking:
    "Weights wᵢ = nᵢ − 3. z̄ = Σwᵢzᵢ ÷ Σwᵢ = {zbar}; Q = Σwᵢ(zᵢ − z̄)² = {q} on {df} degrees of freedom; pooled r = tanh(z̄) = {pooled}",
  sevNeed: 'Enter at least two studies, one per line, as "r, n".',
  // Common
  workingTitle: "How it was calculated",
  apaLabel: "APA-style result",
  copyResult: "Copy result",
  copied: "Copied!",
  significant: "p {p}: statistically significant at the 5% level.",
  notSignificant: "p {p}: not statistically significant at the 5% level.",
  exampleSingleR: "0.45",
  exampleSingleN: "40",
  exampleRho0: "0.2",
  exampleR1: "0.62",
  exampleN1: "85",
  exampleR2: "0.38",
  exampleN2: "110",
  exampleRjk: "0.55",
  exampleRjh: "0.35",
  exampleRkh: "0.40",
  exampleNDep: "100",
  exampleSeveral: "0.30, 50\n0.45, 80\n0.52, 65\n0.20, 120",
} as const;

export type ComparisonStrings =
  typeof CORRELATION_COMPARISON_CALCULATOR_STRINGS;

type Mode = "single" | "independent" | "dependent" | "several";
type FieldKey =
  | "r"
  | "n"
  | "rho0"
  | "r1"
  | "n1"
  | "r2"
  | "n2"
  | "rjk"
  | "rjh"
  | "rkh"
  | "nDep";

const LEVELS = { "0.9": 0.9, "0.95": 0.95, "0.99": 0.99 } as const;
type LevelKey = keyof typeof LEVELS;

const EMPTY: Record<FieldKey, string> = {
  r: "",
  n: "",
  rho0: "",
  r1: "",
  n1: "",
  r2: "",
  n2: "",
  rjk: "",
  rjh: "",
  rkh: "",
  nDep: "",
};

function num(text: string): number {
  return parseNumberInput(text) ?? Number.NaN;
}

export default function CorrelationComparisonCalculatorWidget() {
  const strings = useToolUi(CORRELATION_COMPARISON_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("single");
  const [levelKey, setLevelKey] = useState<LevelKey>("0.95");
  const [alternative, setAlternative] = useState<Alternative>("two-sided");
  const [fields, setFields] = useState<Record<FieldKey, string>>(EMPTY);
  const [several, setSeveral] = useState("");
  const level = LEVELS[levelKey];

  const set = (key: FieldKey) => (value: string) =>
    setFields((current) => ({ ...current, [key]: value }));
  const field = (key: FieldKey, label: string, placeholder: string) => (
    <TextField
      id={`cc-${key}`}
      label={label}
      numeric
      onChange={set(key)}
      placeholder={placeholder}
      value={fields[key]}
    />
  );

  const example = () => {
    if (mode === "single") {
      setFields((f) => ({
        ...f,
        r: strings.exampleSingleR,
        n: strings.exampleSingleN,
        rho0: strings.exampleRho0,
      }));
    } else if (mode === "independent") {
      setFields((f) => ({
        ...f,
        r1: strings.exampleR1,
        n1: strings.exampleN1,
        r2: strings.exampleR2,
        n2: strings.exampleN2,
      }));
    } else if (mode === "dependent") {
      setFields((f) => ({
        ...f,
        rjk: strings.exampleRjk,
        rjh: strings.exampleRjh,
        rkh: strings.exampleRkh,
        nDep: strings.exampleNDep,
      }));
    } else {
      setSeveral(strings.exampleSeveral);
    }
  };
  const clear = () => {
    setFields(EMPTY);
    setSeveral("");
  };

  const studies = several
    .split(LINE_BREAK)
    .filter((line) => line.trim() !== "")
    .map((line) => parseNumberList(line));
  const readable = studies.filter((numbers) => numbers.length >= 2);
  const skipped = studies.length - readable.length;
  const common = {
    fmt,
    strings,
    level,
    levelText: fmt(level, { style: "percent" }),
    alternative,
  };

  return (
    <>
      <section className="space-y-4">
        <PillGroup
          label={strings.modeLabel}
          name="cc-mode"
          onChange={setMode}
          options={[
            { value: "single", label: strings.modeSingle },
            { value: "independent", label: strings.modeIndependent },
            { value: "dependent", label: strings.modeDependent },
            { value: "several", label: strings.modeSeveral },
          ]}
          value={mode}
        />
        {mode === "independent" ? (
          <p className="text-muted-foreground text-sm">
            {strings.independentHint}
          </p>
        ) : null}
        {mode === "dependent" ? (
          <p className="text-muted-foreground text-sm">
            {strings.dependentHint}
          </p>
        ) : null}

        {mode === "single" ? (
          <div className="grid gap-4 sm:grid-cols-3">
            {field("r", strings.rLabel, "0.45")}
            {field("n", strings.nLabel, "40")}
            {field("rho0", strings.rho0Label, "0.2")}
          </div>
        ) : null}
        {mode === "independent" ? (
          <div className="grid gap-4 sm:grid-cols-4">
            {field("r1", strings.r1Label, "0.62")}
            {field("n1", strings.n1Label, "85")}
            {field("r2", strings.r2Label, "0.38")}
            {field("n2", strings.n2Label, "110")}
          </div>
        ) : null}
        {mode === "dependent" ? (
          <div className="grid gap-4 sm:grid-cols-4">
            {field("rjk", strings.rjkLabel, "0.55")}
            {field("rjh", strings.rjhLabel, "0.35")}
            {field("rkh", strings.rkhLabel, "0.40")}
            {field("nDep", strings.nSameLabel, "100")}
          </div>
        ) : null}
        {mode === "several" ? (
          <TextAreaField
            hint={
              <>
                {fmt(readable.length)}
                {tPlural(strings.severalCount, locale, readable.length)}
                {skipped > 0
                  ? tPlural(strings.skipped, locale, skipped, {
                      count: fmt(skipped),
                    })
                  : null}
              </>
            }
            id="cc-several"
            label={strings.severalLabel}
            onChange={setSeveral}
            placeholder={strings.severalPlaceholder}
            value={several}
          />
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="cc-level"
            label={strings.levelLabel}
            onChange={setLevelKey}
            options={[
              { value: "0.9", label: strings.level90 },
              { value: "0.95", label: strings.level95 },
              { value: "0.99", label: strings.level99 },
            ]}
            value={levelKey}
          />
          {mode === "single" ||
          mode === "independent" ||
          mode === "dependent" ? (
            <SelectField
              id="cc-alt"
              label={strings.alternativeLabel}
              onChange={setAlternative}
              options={[
                { value: "two-sided", label: strings.altTwoSided },
                { value: "greater", label: strings.altGreater },
                { value: "less", label: strings.altLess },
              ]}
              value={alternative}
            />
          ) : null}
        </div>
        <div className="flex flex-wrap gap-3">
          <button className={BUTTON_SECONDARY} onClick={example} type="button">
            {strings.tryExample}
          </button>
          <button className={BUTTON_SECONDARY} onClick={clear} type="button">
            {strings.clear}
          </button>
        </div>
      </section>

      {mode === "single" ? (
        <SinglePanel
          {...common}
          n={num(fields.n)}
          r={num(fields.r)}
          rho0={fields.rho0.trim() === "" ? null : num(fields.rho0)}
        />
      ) : null}
      {mode === "independent" ? (
        <IndependentPanel
          {...common}
          n1={num(fields.n1)}
          n2={num(fields.n2)}
          r1={num(fields.r1)}
          r2={num(fields.r2)}
        />
      ) : null}
      {mode === "dependent" ? (
        <DependentPanel
          {...common}
          n={num(fields.nDep)}
          rjh={num(fields.rjh)}
          rjk={num(fields.rjk)}
          rkh={num(fields.rkh)}
        />
      ) : null}
      {mode === "several" ? (
        <SeveralPanel
          {...common}
          ns={readable.map((numbers) => numbers[1])}
          rs={readable.map((numbers) => numbers[0])}
        />
      ) : null}
    </>
  );
}
